/**
 * Trainova AI — useTenantUsers Hook
 */

import { useState, useEffect, useCallback } from 'react';
import { admin } from '../../../services/api';
import { TenantUser } from '../types';

export function useTenantUsers(tenantId?: string) {
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await admin.users.list(tenantId);
      if (res.success && res.data) {
        setUsers(res.data.users || []);
      } else {
        setError(res.error?.message || 'Failed to load organization users.');
      }
    } catch {
      setError('An error occurred while fetching users.');
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const createUser = async (
    data: {
      email: string;
      password: string;
      firstName: string;
      lastName: string;
      role?: string;
      employeeId?: string;
      designation?: string;
      team?: string;
      region?: string;
      grade?: string;
      managerId?: string;
      managerName?: string;
      businessUnit?: string;
      branchCode?: string;
      costCenter?: string;
      hireDate?: string;
      customFields?: Record<string, any>;
      metadata?: Record<string, any>;
    },
    targetTenantId?: string
  ) => {
    const res = await admin.users.create(data, targetTenantId || tenantId);
    if (res.success) {
      await fetchUsers();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to create user.' };
  };

  const bulkImportUsers = async (usersToImport: any[], targetTenantId?: string) => {
    const res = await admin.users.bulkImport(usersToImport, targetTenantId || tenantId);
    if (res.success) {
      await fetchUsers();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to bulk import users.' };
  };

  const updateUser = async (
    userId: string,
    data: {
      firstName?: string;
      lastName?: string;
      role?: string;
      employeeId?: string;
      designation?: string;
      team?: string;
      region?: string;
      grade?: string;
      managerId?: string;
      managerName?: string;
      businessUnit?: string;
      branchCode?: string;
      costCenter?: string;
      hireDate?: string;
      customFields?: Record<string, any>;
      metadata?: Record<string, any>;
      isActive?: boolean;
    },
    targetTenantId?: string
  ) => {
    const res = await admin.users.update(userId, data, targetTenantId || tenantId);
    if (res.success) {
      await fetchUsers();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to update user.' };
  };

  const updateUserRole = async (userId: string, role: string, targetTenantId?: string) => {
    const res = await admin.users.updateRole(userId, role, targetTenantId || tenantId);
    if (res.success) {
      await fetchUsers();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to update user role.' };
  };

  const updateUserStatus = async (userId: string, isActive: boolean, targetTenantId?: string) => {
    const res = await admin.users.updateStatus(userId, isActive, targetTenantId || tenantId);
    if (res.success) {
      await fetchUsers();
      return { success: true, data: res.data };
    }
    return { success: false, error: res.error?.message || 'Failed to update user status.' };
  };

  const resetUserPassword = async (
    userId: string,
    data: { newPassword?: string; generateLinkOnly?: boolean },
    targetTenantId?: string
  ) => {
    const res = await admin.users.resetPassword(userId, data, targetTenantId || tenantId);
    if (res.success) {
      return { success: true, data: res.data, message: res.message || 'Password reset operation succeeded.' };
    }
    return { success: false, error: res.error?.message || 'Failed to reset password.' };
  };

  return {
    users,
    isLoading,
    error,
    refresh: fetchUsers,
    createUser,
    bulkImportUsers,
    updateUser,
    updateUserRole,
    updateUserStatus,
    resetUserPassword,
  };
}
